"use client"

import * as React from "react"
import { OverviewContext, type MembersBoardOverview, type MembersBoardWaitingPerson } from "@ibada/shared-types/overview"

import { useActiveBranchSlug, useActiveWorkspaceSlug, useFormBranchContext } from "@/features/auth"
import { useModulePermission } from "@/features/auth/hooks/use-module-permission"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { HomeTileState, tileStateFor } from "@/features/dashboard/home-d4/lib/home-grid-layout"
import type { QuickConfirm } from "@/features/members/components/board/to-confirm-tile"
import { fullName } from "@/features/dashboard/home-d4/lib/home-format"
import { useQuickConfirmMember } from "@/features/members/hooks/use-quick-confirm-member"
import { useMembersBoardQuery, type MembersBoardContext } from "@/features/members/services/members-board-api"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

export type MembersBoardData = {
  /** Loading, ready, or absent (no permission or a failed read): drives which tiles the board offers. */
  state: HomeTileState
  board: MembersBoardOverview | undefined
  context: MembersBoardContext
  can: { editMembers: boolean }
  /**
   * A branch-relative admin path as a full URL, or null where that page does not exist: the parish context has
   * no members pages, so its tiles carry no members links.
   */
  href: (path: string) => string | null
  /** One-click confirm for a reader who may edit members; null otherwise. */
  confirm: QuickConfirm | null
}

/** Everything the members board tiles read, fetched once for the whole board. */
export function useMembersBoardData(options: { enabled: boolean }): MembersBoardData {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const { activeBranchId, isParishContext } = useFormBranchContext()
  const branchSlug = useActiveBranchSlug() ?? ""
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const href = React.useCallback(
    (path: string) => (isParishContext && path.startsWith("/members") ? null : buildBranchAdminUrl(branchSlug, path, workspaceSlug)),
    [branchSlug, workspaceSlug, isParishContext],
  )
  // Resolved against the active branch: the system branch on the parish board.
  const permission = useModulePermission(WorkspaceAccessModule.MEMBERS)
  const context: MembersBoardContext = isParishContext ? OverviewContext.Parish : OverviewContext.Branch

  const query = useMembersBoardQuery({ workspaceId, context, branchId: activeBranchId }, { enabled: options.enabled })
  const state = tileStateFor(options.enabled, query)

  const { confirm: confirmMember, confirmingId } = useQuickConfirmMember()
  const onConfirm = React.useCallback(
    (person: MembersBoardWaitingPerson) => confirmMember({ id: person.id, name: fullName(person.firstName, person.lastName) }),
    [confirmMember],
  )
  const confirm = React.useMemo<QuickConfirm | null>(
    () => (permission.canEdit ? { onConfirm, confirmingId } : null),
    [permission.canEdit, onConfirm, confirmingId],
  )

  return React.useMemo(
    () => ({ state, board: query.data, context, can: { editMembers: permission.canEdit }, href, confirm }),
    [state, query.data, context, permission.canEdit, href, confirm],
  )
}

const MembersBoardContextValue = React.createContext<MembersBoardData | null>(null)

/** Shares one fetch of the board with every board tile the engine renders. */
export function MembersBoardProvider({ value, children }: { value: MembersBoardData; children: React.ReactNode }) {
  return <MembersBoardContextValue.Provider value={value}>{children}</MembersBoardContextValue.Provider>
}

export function useMembersBoardContext(): MembersBoardData | null {
  return React.useContext(MembersBoardContextValue)
}
